import { useState, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchTasks, uploadPages, startCapture, cancelCapture } from '@/services/redesigns';
import type { RedesignVariant } from '@/types';
import { PageGrid } from './PageGrid';
import { PageDetail } from './PageDetail';
import type { Redesign, RedesignTask } from '@/types';

interface Props {
  redesign: Redesign;
}

type View = { type: 'grid' } | { type: 'page'; pageIndex: number };

interface UploadEntry {
  file: File;
  route: string;
  viewport: 'desktop' | 'mobile' | 'tablet';
  preview: string;
}

/**
 * Main design section — state machine router.
 *
 * Two views:
 *   'grid'  → PageGrid (all pages as thumbnail cards)
 *   'page'  → PageDetail (single page focused view with Stitch/Figma choice)
 */
export function RedesignDesignSection({ redesign }: Props) {
  const [view, setView] = useState<View>({ type: 'grid' });
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadEntries, setUploadEntries] = useState<UploadEntry[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const { data: tasks = [] } = useQuery({
    queryKey: ['redesign-tasks', redesign._id],
    queryFn: () => fetchTasks(redesign._id),
    refetchInterval: (query) => {
      const hasInProgress = query.state.data?.some((t: RedesignTask) => t.status === 'redesigning');
      return hasInProgress ? 5000 : false;
    },
  });

  const uploadMutation = useMutation({
    mutationFn: () => {
      const files = uploadEntries.map(e => e.file);
      const pages = uploadEntries.map(e => ({ route: e.route, viewport: e.viewport }));
      return uploadPages(redesign._id, files, pages);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['redesign', redesign._id] });
      setShowUploadForm(false);
      setUploadEntries([]);
    },
  });

  const captureMutation = useMutation({
    mutationFn: () => startCapture(redesign._id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['redesign', redesign._id] });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => cancelCapture(redesign._id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['redesign', redesign._id] });
    },
  });

  const canUpload = ['captured', 'analyzed'].includes(redesign.status);
  const isEmpty = redesign.pages.length === 0 && redesign.status === 'captured';

  const handleFilesSelected = (files: FileList | null) => {
    if (!files) return;
    const newEntries: UploadEntry[] = [];
    for (const file of Array.from(files)) {
      newEntries.push({
        file,
        route: '/',
        viewport: 'desktop',
        preview: URL.createObjectURL(file),
      });
    }
    setUploadEntries(prev => [...prev, ...newEntries]);
  };

  const removeEntry = (index: number) => {
    setUploadEntries(prev => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const updateEntry = (index: number, updates: Partial<UploadEntry>) => {
    setUploadEntries(prev => prev.map((e, i) => i === index ? { ...e, ...updates } : e));
  };

  // Summary stats
  const stats = useMemo(() => {
    const taskMap = new Map<string, RedesignTask>();
    for (const t of tasks) taskMap.set(`${t.pageRef.route}|${t.pageRef.viewport}`, t);

    let ready = 0, inProgress = 0, done = 0, failed = 0;
    for (const page of redesign.pages) {
      const task = taskMap.get(`${page.route}|${page.viewport}`);
      if (!task) { ready++; continue; }
      if (task.implementationIssue) { done++; continue; }
      if (task.status === 'failed') { failed++; continue; }
      inProgress++;
    }
    return { ready, inProgress, done, failed, total: redesign.pages.length };
  }, [redesign.pages, tasks]);

  if (view.type === 'page') {
    return (
      <PageDetail
        redesign={redesign}
        pageIndex={view.pageIndex}
        onBack={() => setView({ type: 'grid' })}
      />
    );
  }

  // Grid view
  return (
    <div>
      {/* Empty project — show action cards */}
      {isEmpty && !showUploadForm && (
        <div style={{ marginBottom: 32 }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 48, color: '#c7d2fe', marginBottom: 8 }}>photo_library</span>
            <h3 style={{ fontSize: 20, fontWeight: 700, margin: '8px 0 4px', color: 'var(--text-primary)' }}>
              No pages yet
            </h3>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0, maxWidth: 400, marginLeft: 'auto', marginRight: 'auto' }}>
              Start by auto-capturing your live app or manually uploading screenshots.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, maxWidth: 600, margin: '0 auto' }}>
            {/* Start Capture card */}
            {redesign.mode !== 'greenfield' && (
              <button
                onClick={() => captureMutation.mutate()}
                disabled={captureMutation.isPending}
                style={{
                  padding: 32,
                  borderRadius: 16,
                  border: 'none',
                  background: 'linear-gradient(135deg, #4a40e0 0%, #9795ff 100%)',
                  color: '#fff',
                  cursor: captureMutation.isPending ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 12,
                  boxShadow: '0 8px 24px rgba(74, 64, 224, 0.2)',
                  opacity: captureMutation.isPending ? 0.7 : 1,
                  transition: 'all 200ms',
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 32 }}>
                  {captureMutation.isPending ? 'hourglass_top' : 'camera'}
                </span>
                <span style={{ fontWeight: 700, fontSize: 15 }}>
                  {captureMutation.isPending ? 'Starting...' : 'Auto-Capture'}
                </span>
                <span style={{ fontSize: 12, opacity: 0.8 }}>
                  Crawl and screenshot your live app
                </span>
              </button>
            )}

            {/* Upload Screenshots card */}
            <button
              onClick={() => setShowUploadForm(true)}
              style={{
                padding: 32,
                borderRadius: 16,
                border: '2px dashed #c7d2fe',
                background: '#ffffff',
                color: 'var(--text-primary)',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 12,
                transition: 'all 200ms',
                gridColumn: redesign.mode === 'greenfield' ? '1 / -1' : undefined,
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 32, color: 'var(--color-primary)' }}>
                cloud_upload
              </span>
              <span style={{ fontWeight: 700, fontSize: 15 }}>Upload Screenshots</span>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                Manually add page screenshots
              </span>
            </button>
          </div>

          {captureMutation.isError && (
            <div style={{ padding: 10, background: '#fee2e2', borderRadius: 8, color: '#dc2626', fontSize: 13, marginTop: 16, textAlign: 'center' }}>
              Capture failed: {(captureMutation.error as Error).message}
            </div>
          )}
        </div>
      )}

      {/* Pending/capturing state */}
      {(redesign.status === 'pending' || redesign.status === 'capturing') && (
        <div style={{ textAlign: 'center', padding: '48px 16px', color: 'var(--text-secondary)' }}>
          <span className="material-symbols-outlined" style={{ fontSize: 48, color: 'var(--color-primary)', animation: 'pulse 2s infinite' }}>radar</span>
          <p style={{ fontSize: 15, fontWeight: 600, margin: '12px 0 4px' }}>
            {redesign.status === 'pending' ? 'Starting capture...' : 'Capturing pages...'}
          </p>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 16px' }}>
            {redesign.pages.length > 0
              ? `${redesign.pages.length} page${redesign.pages.length !== 1 ? 's' : ''} captured so far.`
              : 'Pages will appear here as they are captured.'}
          </p>
          <button
            onClick={() => cancelMutation.mutate()}
            disabled={cancelMutation.isPending}
            style={{
              padding: '8px 20px',
              borderRadius: 8,
              border: '1px solid #fecaca',
              background: '#fff',
              color: '#dc2626',
              fontSize: 13,
              fontWeight: 600,
              cursor: cancelMutation.isPending ? 'not-allowed' : 'pointer',
              opacity: cancelMutation.isPending ? 0.7 : 1,
            }}
          >
            {cancelMutation.isPending ? 'Canceling...' : 'Cancel Capture'}
          </button>
        </div>
      )}

      {/* Stats summary bar (only show when there are pages) */}
      {redesign.pages.length > 0 && (
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' }}>
        {[
          { icon: 'description', label: 'Total Pages', value: stats.total, iconBg: '#eef2ff', iconColor: '#4a40e0' },
          { icon: 'check_circle', label: 'Complete', value: stats.done, iconBg: '#dcfce7', iconColor: '#15803d' },
          { icon: 'pending', label: 'In Progress', value: stats.inProgress + stats.ready, iconBg: '#e0e7ff', iconColor: '#4338ca' },
          ...(stats.failed > 0 ? [{ icon: 'error', label: 'Failed', value: stats.failed, iconBg: '#fee2e2', iconColor: '#dc2626' }] : []),
        ].map((stat) => (
          <div
            key={stat.label}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              backgroundColor: '#ffffff',
              padding: '14px 20px',
              borderRadius: 12,
              flex: '1 1 0',
              minWidth: 140,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                backgroundColor: stat.iconBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: stat.iconColor,
                flexShrink: 0,
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>{stat.icon}</span>
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#94a3b8', marginBottom: 2 }}>
                {stat.label}
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)' }}>
                {stat.value}
              </div>
            </div>
          </div>
        ))}

        {/* Add Pages button */}
        {canUpload && (
          <button
            onClick={() => setShowUploadForm(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '14px 24px',
              borderRadius: 12,
              border: '2px dashed #c7d2fe',
              background: 'transparent',
              color: 'var(--color-primary)',
              fontWeight: 700,
              fontSize: 13,
              cursor: 'pointer',
              transition: 'all 150ms',
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>add_photo_alternate</span>
            Add Pages
          </button>
        )}
      </div>
      )}

      {/* Upload form */}
      {showUploadForm && (
        <div style={{
          padding: 24,
          marginBottom: 24,
          backgroundColor: '#ffffff',
          borderRadius: 16,
          border: '1px solid #e2e8f0',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Upload Screenshots</h3>
            <button
              onClick={() => { setShowUploadForm(false); setUploadEntries([]); }}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 18 }}
            >
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>

          {/* File picker */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => handleFilesSelected(e.target.files)}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            style={{
              width: '100%',
              padding: 32,
              border: '2px dashed #c7d2fe',
              borderRadius: 12,
              background: '#f8fafc',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
              color: '#64748b',
              marginBottom: uploadEntries.length > 0 ? 16 : 0,
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 32, color: 'var(--color-primary)' }}>cloud_upload</span>
            <span style={{ fontWeight: 600, fontSize: 14 }}>Click to select screenshots</span>
            <span style={{ fontSize: 12, color: '#94a3b8' }}>PNG, JPG — max 10MB each</span>
          </button>

          {/* Entries table */}
          {uploadEntries.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {uploadEntries.map((entry, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    padding: 12,
                    backgroundColor: '#f8fafc',
                    borderRadius: 10,
                  }}
                >
                  {/* Thumbnail */}
                  <img
                    src={entry.preview}
                    alt=""
                    style={{ width: 64, height: 48, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }}
                  />

                  {/* Route input */}
                  <input
                    type="text"
                    value={entry.route}
                    onChange={(e) => updateEntry(idx, { route: e.target.value })}
                    placeholder="/admin/settings"
                    style={{
                      flex: 1,
                      padding: '6px 10px',
                      border: '1px solid var(--color-border)',
                      borderRadius: 6,
                      fontSize: 13,
                      fontFamily: 'monospace',
                    }}
                  />

                  {/* Viewport select */}
                  <select
                    value={entry.viewport}
                    onChange={(e) => updateEntry(idx, { viewport: e.target.value as 'desktop' | 'mobile' | 'tablet' })}
                    style={{
                      padding: '6px 10px',
                      border: '1px solid var(--color-border)',
                      borderRadius: 6,
                      fontSize: 13,
                    }}
                  >
                    <option value="desktop">Desktop</option>
                    <option value="mobile">Mobile</option>
                    <option value="tablet">Tablet</option>
                  </select>

                  {/* Remove button */}
                  <button
                    onClick={() => removeEntry(idx)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#94a3b8',
                      padding: 4,
                    }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>delete</span>
                  </button>
                </div>
              ))}

              {/* Upload button */}
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button
                  onClick={() => uploadMutation.mutate()}
                  disabled={uploadMutation.isPending || uploadEntries.some(e => !e.route.trim())}
                  style={{
                    padding: '10px 24px',
                    borderRadius: 10,
                    border: 'none',
                    background: 'linear-gradient(135deg, #4a40e0 0%, #9795ff 100%)',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 13,
                    cursor: uploadMutation.isPending ? 'not-allowed' : 'pointer',
                    opacity: uploadMutation.isPending ? 0.7 : 1,
                  }}
                >
                  {uploadMutation.isPending ? 'Uploading...' : `Upload ${uploadEntries.length} Screenshot${uploadEntries.length > 1 ? 's' : ''}`}
                </button>
              </div>

              {uploadMutation.isError && (
                <div style={{ padding: 10, background: '#fee2e2', borderRadius: 8, color: '#dc2626', fontSize: 13 }}>
                  Upload failed: {(uploadMutation.error as Error).message}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {redesign.error && (
        <div style={{ padding: 12, background: '#fee2e2', borderRadius: 8, color: 'var(--color-error)', marginBottom: 16, fontSize: 13 }}>
          {redesign.error}
        </div>
      )}

      <PageGrid
        pages={redesign.pages}
        tasks={tasks}
        onSelectPage={(idx) => setView({ type: 'page', pageIndex: idx })}
      />
    </div>
  );
}

// Keep these exports for backward compatibility (used by other components that may import from here)
export function HtmlExportSection({ html }: { html: string }) {
  const [showSource, setShowSource] = useState(false);
  const handleOpenInTab = () => {
    const win = window.open('', '_blank');
    if (win) { win.document.write(html); win.document.close(); }
  };

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>HTML Export</h4>
        <button onClick={handleOpenInTab} style={{ padding: '4px 12px', background: '#6366f1', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 500 }}>Open in new tab</button>
        <button onClick={() => setShowSource(!showSource)} style={{ padding: '4px 12px', background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--color-border)', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>
          {showSource ? 'Hide source' : 'View source'}
        </button>
      </div>
      {showSource && <pre style={{ padding: 12, background: '#1e1b4b', color: '#e9ecef', borderRadius: 6, overflow: 'auto', maxHeight: 400, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{html}</pre>}
    </div>
  );
}

export function VariantCard({ variant, isSelecting, onSelect }: { variant: RedesignVariant; isSelecting: boolean; onSelect: () => void }) {
  return (
    <div style={{ border: variant.selected ? '2px solid #6366f1' : '1px solid var(--color-border-light)', borderRadius: 8, overflow: 'hidden', background: variant.selected ? '#eef2ff' : 'white' }}>
      {variant.imageUrl ? (
        <img src={variant.imageUrl} alt={variant.label || `Variant ${variant.variantIndex}`} style={{ width: '100%', height: 180, objectFit: 'cover' }} />
      ) : (
        <div style={{ width: '100%', height: 180, background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6c757d' }}>No preview</div>
      )}
      <div style={{ padding: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 13, fontWeight: 500 }}>{variant.label || `Variant ${variant.variantIndex + 1}`}</span>
        <button onClick={onSelect} disabled={variant.selected || isSelecting} style={{ padding: '4px 12px', fontSize: 12, borderRadius: 4, border: 'none', background: variant.selected ? 'var(--color-success)' : '#6366f1', color: 'white', cursor: variant.selected ? 'default' : 'pointer' }}>
          {variant.selected ? 'Selected' : 'Select'}
        </button>
      </div>
    </div>
  );
}
